import * as angular from 'angular';
import * as _ from 'lodash';

import { IHistorianStorageConfigurationService, IConnectionPointService, IGroupService } from '../../services';
import { exportConnectionPointTypes, connectionPointTypes, guidEmpty } from '../../common';
import { Group, HistorianStorageConfiguration, ConnectionPointType, ConnectionPointDTO, Miniature, IApprovalStrategyLevel, NamedQueriesDto, Dictionary, ConnectionPoint } from '../../models';
import { IApprovalStrategyService } from '../../services';
import { IQuerySetService } from './querySet.service';

declare type GroupWithParent = Group & { parent: GroupWithParent };

export interface ExportContextualConfiguration {
    allConnectionPointTypes: { [key: number]: string };
    configurations: HistorianStorageConfiguration[];
    connectionPoints: Dictionary<ConnectionPoint>
    groups: GroupWithParent[];
    levels: { key: number, value: string; }[];
    namedQueries: Dictionary<NamedQueriesDto>,
    widgetMiniatures: Miniature[];
}

export interface IExportConfigurationService {
    getConfiguration(): angular.IPromise<ExportContextualConfiguration>;
}

export class ExportConfigurationService implements IExportConfigurationService {
    static serviceId = 'exportConfigurationService';

    static $inject = [
        '$q',
        '$translate',
        'connectionPointService',
        'groupService',
        'historianStorageService',
        'approvalStrategyService',
        'querySetService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _connectionPointService: IConnectionPointService,
        private _groupService: IGroupService,
        private _configurationService: IHistorianStorageConfigurationService,
        private _approvalStrategyService: IApprovalStrategyService,
        private _querySetService: IQuerySetService
    ) {
    }

    // TODO : this needs to be refactored to use store
    getConfiguration(): angular.IPromise<ExportContextualConfiguration> {
        const allConnectionPointTypes = connectionPointTypes.reduce((map, obj) => {
            map[obj.key] = this._$translate.instant(obj.value);
            return map;
        }, {});
        return this._$q
            .all([
                this._configurationService.getAll(),
                this._connectionPointService.getAll(),
                this._groupService.getAll(),
                this._approvalStrategyService.getAll(),
                this._querySetService.getNamedQueries()
            ])
            .then(data => {
                const configurations = data[0];
                const connectionPoints: Dictionary<ConnectionPoint> = {};
                data[1].forEach(dto => connectionPoints[dto.connectionPoint.guid] = dto.connectionPoint);
                const widgetMiniatures = this.getMiniatures(data[1], allConnectionPointTypes);
                const all = this._groupService.convertToTree(data[2].map(d => d.group));
                const groups: any[] = all.filter(d => d.approvalStrategyGuid && d.approvalStrategyGuid !== guidEmpty);
                const groupWithParents = this.flattenGroupList(groups);
                const strategies = data[3];
                const namedQueries = data[4];

                const levels = []
                    .concat(...strategies.map(s => s.approbationStrategyLevelCollection))
                    .map((d: IApprovalStrategyLevel) => d.level);
                const res = _.sortBy(_.uniq(levels))
                    .map(d => ({
                        key: d,
                        value: `${this._$translate.instant('Level')} ${d + 1}`
                    }));

                return {
                    allConnectionPointTypes,
                    configurations,
                    widgetMiniatures,
                    levels: res,
                    groups: groupWithParents,
                    namedQueries: namedQueries,
                    connectionPoints
                } as ExportContextualConfiguration;
            });
    }

    private getMiniatures(data: ConnectionPointDTO[], cpTypes: { [key: number]: string }): Miniature[] {
        const labelType = this._$translate.instant('Type');
        const labelAddress = this._$translate.instant('Address');
        return data
            .filter(dto => this.canExport(dto))
            .map(e => e.connectionPoint)
            .map(cp => {
                return {
                    title: cp.name,
                    type: 'connectionPoint',
                    highlights: [
                        `${labelType} : ${cpTypes[cp.type]}`,
                        `${labelAddress}: ${cp.address}`],
                    icon: 'fa-connectdevelop',
                    data: {
                        type: cp.type,
                        guid: cp.guid,
                        name: cp.name
                    },
                    create: undefined,
                    group: undefined,
                    sortingOrder: 0
                };
            });
    }

    private flattenGroupList(groups: GroupWithParent[]): GroupWithParent[] {
        let result = _.flatten(groups.map(this.flatten));
        result = _.uniq(result);
        result = _.sortBy(result, r => r.uniqueName);
        return result;
    }

    private flatten(group: GroupWithParent): GroupWithParent[] {
        const result = [];
        let g = group;
        while (g.guid !== guidEmpty) {
            result.push(g);
            g = g.parent;
        }
        return result;
    }

    private canExport(dto: ConnectionPointDTO): boolean {
        const cp = dto?.connectionPoint;
        let canExport = exportConnectionPointTypes.indexOf(dto.connectionPoint.type) > -1;

        switch (cp.type) {
            case ConnectionPointType.PIAF:
                canExport = !cp?.instance;
                break;
            case ConnectionPointType.CSVExport:
                canExport = !!cp?.address;
                break;
            case ConnectionPointType.SQL:
                canExport = cp.querySetGuid && cp.querySetGuid !== guidEmpty;
                break;
        }

        return canExport;
    }
}